import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import CardChallenge from 'src/app/ui/components/molecules/library-challenges/card-challenge';

import type { Challenge } from 'app/types/graphql';

import EmptyState from 'app/ui/components/molecules/empty-state';
import GhostIcon from 'app/ui/components/atoms/ghost';

const CollectionsArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  gap: 2.5rem;
  margin-top: 2rem;
`;

const CollectionCardWrapper = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 4rem;
  width: 100%;
  align-items: stretch;
  grid-template-columns: repeat(2, 1fr);
    ${({ theme: { media } }) => `${media.large(
      () => `
        grid-auto-flow: column;
        grid-auto-columns: minmax(200px, 1fr);
        grid-template-columns: none;
      `,
    )}
  `}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
`;

const PageTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

type LibraryChallengesProps = {
  challenges: Challenge[];
};

const LibraryChallenges: React.FC<LibraryChallengesProps> = ({ challenges }) => {
  const theme = useTheme();
  const intl = useIntl();

  const libraryChallengesHeader = intl.formatMessage({ id: 'library.challenges' });
  const challengesCopy = intl.formatMessage({ id: 'library.challengesCopy' });

  const emptyStateTitle = intl.formatMessage({ id: 'library.noChallenges' });

  const filteredChallenges = challenges.filter((challenge, index, self) => {
    const conceptName = challenge.lesson?.concept?.name;
    if (!conceptName || conceptName === 'Custom') {
      return false;
    }
    return index === self.findIndex(({ lesson }) => lesson?.concept?.name === conceptName);
  });

  return (
    <CollectionsArea>
      <TitleWrapper>
        <PageTitle variant="h3" color={theme.colors.white}>
          {libraryChallengesHeader}
        </PageTitle>
        <Typography variant="body2" color={theme.colors.lightGray}>
          {challengesCopy}
        </Typography>
      </TitleWrapper>
      {filteredChallenges.length > 0 ? (
        <CollectionCardWrapper>
          {filteredChallenges.map(({ id, lesson }) => (
            <CardChallenge
              key={id}
              challengeId={id}
              conceptName={lesson?.concept?.name || ''}
              concept={lesson?.concept}
            />
          ))}
        </CollectionCardWrapper>
      ) : (
        <EmptyState icon={<GhostIcon />} heading={emptyStateTitle} />
      )}
    </CollectionsArea>
  );
};

export default LibraryChallenges;
